import React, { useEffect, useState } from 'react';
import InputField from 'components/fields/InputField';

const AddQA = ({ setTestData, testData, setDisable }) => {
  const [errors, setErrors] = useState([]);
  const [completionTime, setCompletionTime] = useState('60 minutes'); //testData.completionTime ||
  const [completionTimeInMinutes, setCompletionTimeInMinutes] = useState(60);

  const convertToMinutes = (value, unit) => {
    const numValue = parseFloat(value);
    switch (unit) {
      case 'hour':
        return numValue * 60;
      case 'day':
        return numValue * 1440;
      case 'minute':
      default:
        return numValue;
    }
  };

  const handleCompletionTimeChange = (event) => {
    const { name, value } = event.target;

    setTestData((prevData) => {
      const [prevValue, prevUnit] = completionTime.split(' ');
      let updatedValue = prevValue;
      let updatedUnit = prevUnit || 'hour';

      if (name === 'value') {
        updatedValue = value;
      }
      if (name === 'unit') {
        updatedUnit = value;
      }

      const minutesValue = convertToMinutes(updatedValue, updatedUnit);

      setCompletionTime(`${updatedValue} ${updatedUnit}`);
      setCompletionTimeInMinutes(minutesValue);

      return {
        ...prevData,
        completionTimeInMinutes: minutesValue,
      };
    });
  };

  const handleInputChange = (event, index, field, optionIndex) => {
    const updatedQuestions = [...testData.questions];

    if (field === 'options') {
      updatedQuestions[index].options[optionIndex] = event.target.value;
    } else {
      updatedQuestions[index][field] = event.target.value;
    }

    setTestData({ ...testData, questions: updatedQuestions });
  };

  const handleTitleChange = (event) => {
    setTestData((prevData) => ({
      ...prevData,
      title: event.target.value,
    }));
  };

  const addQuestion = () => {
    const newQuestion = {
      questionText: '',
      options: ['', '', '', ''],
      correctAnswer: '',
    };

    setTestData((prevData) => ({
      ...prevData,
      questions: [...prevData.questions, newQuestion],
    }));
  };

  const removeQuestion = (index) => {
    const updatedQuestions = testData.questions.filter((_, i) => i !== index);
    setTestData((prevData) => ({
      ...prevData,
      questions: updatedQuestions,
    }));
  };

  useEffect(() => {
    const newErrors = [];

    if (!testData.title.trim()) {
      newErrors.push('Test title is required.');
    }

    testData.questions.forEach((question, index) => {
      if (!question.questionText.trim()) {
        newErrors.push(`Question ${index + 1} is incomplete.`);
      }
      if (
        testData.type === 'mcq' &&
        question.options.some((option) => !option.trim())
      ) {
        newErrors.push(`Question ${index + 1} has empty MCQ options.`);
      }
      if (question.correctAnswer === '') {
        newErrors.push(`Question ${index + 1} has no correct answer.`);
      }
    });

    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [testData, setDisable]);

  return (
    <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
      <h4 className="pt-[5px] text-xl font-bold text-navy-700 dark:text-white">
        Test Information
      </h4>

      {/* Test Title Input */}
      <InputField
        extra="mb-3"
        label="Test Title"
        placeholder="Enter Test Title"
        id="testTitle"
        type="text"
        onChange={handleTitleChange}
        value={testData?.title}
      />

      {/* Completion Time Selector */}
      <div className="mb-3">
        <label className="text-sm font-medium text-gray-700">
          Completion Time
        </label>
        <div className="flex items-center gap-3">
          <input
            name="value"
            type="text"
            min="0.5"
            step="0.5"
            placeholder="Enter Time"
            className="rounded border border-gray-300 p-2 dark:!bg-navy-700"
            // onChange={handleCompletionTimeChange}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*\.?\d*$/.test(value)) {
                handleCompletionTimeChange(e);
              }
            }}
            value={completionTime.split(' ')[0] || ''}
          />

          <select
            name="unit"
            className="rounded border border-gray-300 p-2 dark:!bg-navy-700"
            onChange={handleCompletionTimeChange}
            value={completionTime.split(' ')[1] || 'hour'}
          >
            <option value="minute">Minutes</option>
            <option value="hour">Hours</option>
            <option value="day">Days</option>
          </select>
        </div>

        {/* Display converted time */}
        <p>Time in Minutes: {completionTimeInMinutes} minutes</p>
      </div>

      {/* Questions List */}
      {testData.questions.map((question, index) => (
        <div key={index} className="mt-5">
          <h5 className="text-md font-semibold">Question {index + 1}</h5>

          {/* Question Text Input */}
          <InputField
            extra="mb-3"
            label="Question Text"
            placeholder="Enter Question Text"
            type="text"
            onChange={(e) => handleInputChange(e, index, 'questionText')}
            value={question?.questionText}
          />

          {/* Correct Answer Input */}
          <InputField
            extra="mb-3"
            label="Correct Answer"
            placeholder="Enter Correct Answer"
            type="text"
            onChange={(e) => handleInputChange(e, index, 'correctAnswer')}
            value={question?.correctAnswer}
          />
          <InputField
            extra="mb-3"
            label="Marks"
            placeholder="Enter Marks"
            type="text"
            value={question?.marks}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*\.?\d*$/.test(value)) {
                handleInputChange(e, index, 'marks');
              }
            }}
            className="w-full rounded border p-2"
          />

          {/* Remove Question Button */}
          <button
            className="mt-2 text-red-500"
            onClick={() => removeQuestion(index)}
          >
            Remove Question
          </button>
        </div>
      ))}

      {/* Add Question Button */}
      <button
        className="mt-5 rounded  bg-brand-900 px-16 px-4 py-2 py-2 font-medium text-white text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
        onClick={addQuestion}
      >
        + Add Question
      </button>

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="mt-4 text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddQA;
